// NewLeadsTable.jsx
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import {
  formatDate,
  MappingStatus,
  MappingStyles,
} from "../../../../utils/utils";
import { crmAPI } from "../../../../services/AdminPortal/crmAPI";
import { toast } from "react-toastify";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";

const LeadGenerationDropdown = ({ applicant, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [position, setPosition] = useState(null);

  const leadModes = [
    "Instagram",
    "Facebook",
    "What's app",
    "Referral",
    "Google ads",
    "Traditional Marketing",
    "Media Marketing",
    "Affiliate Marketing",
    "Influencers",
    "Campaigns",
    "Cold calls",
    "Gmail marketing",
    "Walk-in",
  ];

  const handleModeSelect = async (mode) => {
    setLoading(true);
    try {
      await onUpdate(applicant._id, mode);
      setIsOpen(false);
    } catch (error) {
      console.error("Error updating lead generation mode:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleDropdown = (e) => {
    const isOpening = !isOpen;
    setIsOpen(isOpening);

    if (isOpening) {
      const rect = e.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 200; // approx dropdown height
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      setPosition({
        top: rect.bottom + 8,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 160)),
        openUpward: spaceBelow < dropdownHeight && spaceAbove > dropdownHeight,
      });
    }
  };

  // close on outside click and scroll
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest("[data-lead-dropdown]")) {
        setIsOpen(false);
      }
    };
    const handleScroll = () => {
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  return (
    <div className="relative">
      <button
        onClick={toggleDropdown}
        data-lead-dropdown
        className="flex items-center gap-2 px-3 py-1 bg-[#131313] border border-[#333333] rounded text-sm text-white hover:bg-[#2a2a2a]"
        disabled={loading}
      >
        {applicant.leadGeneration || "Select Mode"}
        <ChevronDown size={14} />
      </button>

      {isOpen && position && (
        <div
          data-lead-dropdown
          className="fixed z-[9999] w-40 bg-[#131313] border border-[#333333] rounded shadow-lg max-h-32 overflow-y-auto"
          style={{
            top: position.openUpward ? "auto" : position.top + "px",
            bottom: position.openUpward
              ? window.innerHeight - position.top + "px"
              : "auto",
            left: position.left,
          }}
        >
          {leadModes.map((mode) => (
            <button
              key={mode}
              onClick={() => handleModeSelect(mode)}
              className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#1f1f1f] transition-colors"
              disabled={loading}
            >
              {mode}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};


export default function NewLeadsTable({
  loading,
  staff,
  selectedAll,
  handleSelectApplicant,
  filteredApplicants,
  selectedApplicants,
  handleSelectAll,
  openAssignmentModal,
}) {
const handleLeadGenerationUpdate = async (loanId, mode) => {
  try {
    await crmAPI.updateLeadGeneration(loanId, mode);
    toast.success(`Lead generation mode updated to ${mode}`);

    // 🔄 Reload page after success
    setTimeout(() => {
      window.location.reload();
    }, 1000); // small delay so toast can show
  } catch (error) {
    toast.error('Failed to update lead generation mode');
    throw error;
  }
};

  const { user } = useAuth();
  const navigate = useNavigate();
  const [dropdownOpen, setDropdownOpen] = useState({});
  const [dropdownPosition, setDropdownPosition] = useState({});

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        !event.target.closest("[data-dropdown-id]") &&
        !event.target.closest("[data-dropdown-menu]")
      ) {
        setDropdownOpen({});
      }
    };
    const handleScroll = (event) => {
      // Close dropdowns when scrolling outside of them
      if (
        !event.target.closest("[data-dropdown-id]") &&
        !event.target.closest("[data-dropdown-menu]")
      ) {
        setDropdownOpen({});
      }
    };
    
    const handleWindowScroll = () => {
      // Close all dropdowns on window scroll
      setDropdownOpen({});
    };
    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("scroll", handleWindowScroll);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("scroll", handleWindowScroll);
    };
  }, []);

  const toggleDropdown = (applicantId, event) => {
    const isOpening = !dropdownOpen[applicantId];
    setDropdownOpen({ [applicantId]: isOpening });

    if (isOpening && event) {
      const rect = event.currentTarget.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 160;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;

      setDropdownPosition((prev) => ({
        ...prev,
        [applicantId]: {
          top: rect.bottom + 8,
          left: Math.max(
            8,
            Math.min(rect.left - 48, window.innerWidth - 152)
          ),
          openUpward:
            spaceBelow < dropdownHeight && spaceAbove > dropdownHeight,
        },
      }));
    }
  };

  const handleStaffSelect = (applicant, staffMember) => {
    console.log("Staff selected:", { applicant, staffMember });
    setDropdownOpen({}); // Close dropdown

    // Call parent handler to open confirmation modal
    openAssignmentModal(applicant, staffMember);
  };

  // Generate & share application PDF
  const handleShareApplication = async (applicationId) => {
    try {
      toast.info("Generating PDF... Please wait.");
      const result = await crmAPI.generateLoanApplicationPDF(applicationId);

      if (result.success) {
        toast.success(`PDF generated successfully! File: ${result.filename}`);
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <div className="bg-[#131313] rounded-lg border border-[#333333] w-full overflow-x-auto scrollbar-hide">
      <table className="min-w-full">
        <thead className="border-y border-[#333333]">
          <tr>
            <th className="p-4 text-center border-2 border-[#333333]">
              <input
                type="checkbox"
                className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                  before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                checked={selectedAll}
                onChange={handleSelectAll}
              />
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">DATE</th>
            <th className="p-4 text-center border-2 border-[#333333]">
              APPLICANT NAME
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              LOAN AMOUNT
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">STATUS</th>
            <th className="p-4 text-center border-2 border-[#333333]">
              MODE OF LEAD GENERATION
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              ASSIGNED TO
            </th>
            <th className="p-4 text-center border-2 border-[#333333]">
              ACTIONS
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredApplicants.map((applicant) => (
            <tr key={applicant._id}>
              {/* Checkbox */}
              <td className="p-4 text-center border-2 border-[#333333]">
                <input
                  type="checkbox"
                  className="appearance-none h-5 w-5 border-2 border-white rounded-sm bg-transparent checked:bg-yellow-400 checked:border-yellow-400 checked:appearance-none relative cursor-pointer
                    before:content-['✓'] before:absolute before:text-white before:inset-0 before:flex before:items-center before:justify-center before:text-sm before:font-bold before:opacity-0 checked:before:opacity-100"
                  checked={selectedApplicants.includes(applicant._id)}
                  onChange={() => handleSelectApplicant(applicant._id)}
                />
              </td>

              {/* Date */}
              <td className="p-4 text-center border-2 border-[#333333]">
                {formatDate(applicant.createdAt)}
              </td>

              {/* Applicant Name */}
              <td
                className={`p-4 text-center border-2 border-[#333333] ${
                  applicant.referredBy !== null ? "flex flex-col gap-1" : ""
                }`}
              >
                {applicant.user?.name || applicant?.username || "N/A"}
                {applicant.referredBy !== null && (
                  <span className="text-xs text-gray-400">
                    <span className="text-xs text-[#FFCA20]">
                      Affiliate Application
                    </span>
                  </span>
                )}
              </td>

              {/* Loan Amount */}
              <td className="p-4 text-center border-2 border-[#333333]">
                <div>
                  <div>{applicant.principle}</div>
                  <div className="text-sm text-gray-400">
                    {applicant.loanType}
                  </div>
                </div>
              </td>

              {/* Status */}
              <td className="p-2 lg:p-4 text-center border-2 border-[#333333] min-w-[80px] lg:min-w-[120px]">
                <span
                  className={`px-2 lg:px-3 py-1 rounded-full text-xs lg:text-sm whitespace-nowrap ${MappingStyles(
                    applicant.status
                  )}`}
                >
                  {MappingStatus(applicant.status)}
                </span>
              </td>

              {/* Lead Generation */}
              <td className="p-4 text-center border-2 border-[#333333]">
                <LeadGenerationDropdown 
                  applicant={applicant}
                  onUpdate={(loanId, mode) => handleLeadGenerationUpdate(loanId, mode)}
                />
              </td>

              {/* Assigned To */}
              <td className="p-4 text-center border-2 border-[#333333]">
                <div className="flex items-center gap-2">
                  <span
                    className="cursor-pointer"
                    data-dropdown-id={applicant._id}
                    onClick={(e) => toggleDropdown(applicant._id, e)}
                  >
                    {applicant.assignedTo?.name || "Assign to"}
                  </span>

                  {dropdownOpen[applicant._id] && (
                    <div
                      data-dropdown-menu
                      className="fixed z-[9999] w-36 rounded shadow-lg border border-[#333333] bg-[#131313] max-h-32 overflow-y-auto"
                      style={{
                        top: dropdownPosition[applicant._id]?.openUpward
                          ? "auto"
                          : (dropdownPosition[applicant._id]?.top || 0) + "px",
                        bottom: dropdownPosition[applicant._id]?.openUpward
                          ? window.innerHeight -
                            (dropdownPosition[applicant._id]?.top || 0) +
                            "px"
                          : "auto",
                        left: dropdownPosition[applicant._id]?.left || 0,
                      }}
                    >
                      {loading ? (
                        <div className="px-3 py-2 text-sm text-white">Loading...</div>
                      ) : (
                        staff.map((staffMember, idx) => (
                          <button
                            key={idx}
                            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-[#1f1f1f] transition-colors"
                            onClick={() =>
                              handleStaffSelect(applicant, staffMember)
                            }
                          >
                            {staffMember.name}
                          </button>
                        ))
                      )}
                    </div>
                  )}

                  <ChevronDown
                    data-dropdown-id={applicant._id}
                    onClick={(e) => toggleDropdown(applicant._id, e)}
                    size={18}
                    className="text-gray-400 cursor-pointer"
                  />
                </div>
              </td>

              {/* Actions */}
              <td className="p-4 text-center border-2 border-[#333333]">
                <button
                  className="text-[#FFCA20] mr-2 hover:text-[#FFCA20]"
                  onClick={() => {
                    const role = user?.role?.toLowerCase();
                    if (["admin", "subadmin"].includes(role)) {
                      navigate(
                        `/${role}/loan-applications/approval/${applicant._id}`
                      );
                    } else {
                      console.warn("Unknown role:", role);
                    }
                  }}
                >
                  View
                </button>
                <button
                  className="text-[#FFCA20] ml-2 hover:text-[#FFCA20]"
                  onClick={() => handleShareApplication(applicant._id)}
                >
                  <svg
                    width="16"
                    height="15"
                    viewBox="0 0 16 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.75 5.78125H13.2188L9.25 2.34375C8.9375 2.0625 8.90625 1.59375 9.15625 1.28125C9.4375 0.96875 9.90625 0.9375 10.2188 1.1875L15.7188 5.9375C15.875 6.09375 16 6.3125 16 6.53125C16 6.71875 15.875 6.9375 15.7188 7.09375L10.2188 11.8438C10.0938 11.9688 9.90625 12.0312 9.75 12.0312C9.53125 12.0312 9.3125 11.9375 9.15625 11.75C8.90625 11.4375 8.9375 10.9688 9.25 10.6875L13.2188 7.28125H4.75C2.9375 7.28125 1.5 8.71875 1.5 10.5312V14.2812C1.5 14.6875 1.15625 15 0.75 15C0.3125 15 0 14.6875 0 14.2812V10.5312C0 7.90625 2.125 5.78125 4.75 5.78125Z"
                      fill="#FFCA20"
                    />
                  </svg>
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
